import { Spin } from 'antd';
import moment from 'moment';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import { AxiosSingleton } from '../../utils/axiosinstance';
import { message } from '../../utils/custommessage';

export type TableColumnType = {
  title: string;
  width: string;
  dataIndex: string;
  key: string;
  fixed?: string;
  render: (value: any, data: any) => any;
};

const PreviewCreative = ({ imageUrl }: { imageUrl: string }) => {
  if (!imageUrl) {
    return <span>-</span>;
  }

  return (
    <div>
      <img src={imageUrl} alt="Preview" style={{ width: '100%' }} />
    </div>
  );
};

const renderImpressions = (impressions) => (
  <div style={{ display: 'flex', justifyContent: 'center' }}>
    {impressions || (impressions === null || impressions === 0 ? '-' : <Spin />)}
  </div>
);

const MastheadControls = ({ campaign }: { campaign: any }) => {
  const axios = AxiosSingleton.axiosInstance;
  const navigate = useNavigate();
  const { user } = useAuth();
  const edit = (campaignId, direct = false) => {
    navigate(`/masthead-campaigns/edit/${campaignId}${direct ? '?direct=1' : ''}`);
  };

  const activate = (campaign) => {
    const requests = [
      axios.post('/masthead-campaigns/change-status', { ids: [campaign.id], status: 1 })
    ];
    if (campaign.legacy_masthead_id) {
      requests.push(
        axios.post('/masthead/change-status', {
          ids: [campaign.legacy_masthead_id],
          status: 1
        })
      );
    }
    Promise.all(requests)
      .then(() => {
        message.success('Campaign successfully activated!');
        setTimeout(() => {
          navigate(0);
        }, 300);
      })
      .catch(({ response }) => {
        const { data } = response || {};
        if (data?.error) {
          message.error(data.error);
        } else {
          message.error('Something went wrong');
        }
      });
  };

  const deactivate = (campaign) => {
    const requests = [
      axios.post('/masthead-campaigns/change-status', { ids: [campaign.id], status: 0 })
    ];
    if (campaign.legacy_masthead_id) {
      requests.push(
        axios.post('/masthead/change-status', {
          ids: [campaign.legacy_masthead_id],
          status: 0
        })
      );
    }
    Promise.all(requests)
      .then(() => {
        message.success('Campaign successfully deactivated!');
        setTimeout(() => {
          navigate(0);
        }, 300);
      })
      .catch(({ response }) => {
        const { data } = response || {};
        if (data?.error) {
          message.error(data.error);
        } else {
          message.error('Something went wrong');
        }
      });
  };

  return (
    <div className="report-name-cell">
      <div className="report-name">{campaign.title_en}</div>
      <div className="report-name-actions">
        <span
          className="report-name-action"
          onClick={() => {
            if (campaign.legacy_masthead_id) {
              edit(campaign.legacy_masthead_id);
            } else {
              edit(campaign.id, true);
            }
          }}
        >
          Edit
        </span>
        {user?.publisher_id === 2 &&
          (Boolean(campaign.status) ? (
            <span
              className="report-name-action"
              onClick={() => {
                deactivate(campaign);
              }}
            >
              Deactivate
            </span>
          ) : (
            <span
              className="report-name-action"
              onClick={() => {
                activate(campaign);
              }}
            >
              Activate
            </span>
          ))}
      </div>
    </div>
  );
};

const placementLabels = {
  home: 'Home',
  search: 'Search'
};

export const mastheadCampaignsTableColumn: TableColumnType[] = [
  {
    title: 'Name',
    dataIndex: 'title_en',
    key: '0',
    width: '9rem',
    render: (_, data) => <MastheadControls campaign={data} />
  },
  {
    title: 'Creative',
    dataIndex: 'banner_url_en',
    key: '1',
    width: '9rem',
    render: (imageUrl) => <PreviewCreative imageUrl={imageUrl} />
  },
  {
    title: 'Creative (Arabic)',
    dataIndex: 'banner_url_ar',
    key: '2',
    width: '9rem',
    render: (imageUrl) => <PreviewCreative imageUrl={imageUrl} />
  },
  {
    title: 'Creative (French)',
    dataIndex: 'banner_url_fr',
    key: '3',
    width: '9rem',
    render: (imageUrl) => <PreviewCreative imageUrl={imageUrl} />
  },
  {
    title: 'Selected Date',
    dataIndex: 'day',
    key: '4',
    width: '6rem',
    render: (startDate: string) => <span>{moment(startDate).format('MMM DD, YYYY')}</span>
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: '5',
    width: '6rem',
    render: (_, campaign) => <span>{Boolean(campaign.status) ? 'Active' : 'Inactive'}</span>
  },
  {
    title: 'Countries',
    dataIndex: 'countries',
    key: '6',
    width: '6rem',
    render: (countries) => <span>{countries.join(', ')}</span>
  },
  {
    title: 'Placement',
    dataIndex: 'placement',
    key: '6b',
    width: '6rem',
    render: (placement: string[]) => (
      <span>{(placement || []).map((p) => placementLabels[p] || p).join(', ')}</span>
    )
  },
  {
    title: 'Unique Clicks',
    dataIndex: 'unique_clicks',
    key: '7',
    width: '6rem',
    render: renderImpressions
  },
  {
    title: 'Clicks',
    dataIndex: 'clicks',
    key: '8',
    width: '6rem',
    render: renderImpressions
  },
  {
    title: 'Unique Impressions',
    dataIndex: 'unique_impressions',
    key: '9',
    width: '6rem',
    render: renderImpressions
  },
  {
    title: 'Impressions',
    dataIndex: 'impressions',
    key: '10',
    width: '6rem',
    render: renderImpressions
  }
];
